import { apiCall } from "../../utils/servicesUtils"

const BASE_URL = process.env.REACT_APP_BACKEND_URL

export const getBQTableSchema = (
    bqProjectId: string,
    bqDatasetId: string,
    bqTableName: string,
    projectId: string
): Promise<any> => {
    const urlFetch = `${BASE_URL}/api/bigquery/schema`
    return apiCall(
        urlFetch,
        'POST',
        JSON.stringify({
            bq_project_id: bqProjectId,
            bq_dataset_id: bqDatasetId,
            bq_table_name: bqTableName,
            project_id: projectId
        }),
        false
    )
}

export const getBQTablePreview = (
    bqProjectId: string,
    bqDatasetId: string,
    bqTableName: string,
    columns: string[],
    promptRules: PromptRule[],
    projectId: string
): Promise<any> => {
    const urlFetch = `${BASE_URL}/api/bigquery/preview`
    return apiCall(
        urlFetch,
        'POST',
        JSON.stringify({
            project_id: projectId,
            bq_project_id: bqProjectId,
            bq_dataset_id: bqDatasetId,
            bq_table_name: bqTableName,
            columns: columns,
            prompt_rules: promptRules
        }),
        false
    )
}

export const uploadReferenceImagesBulk = (
    files: File[],
    projectId: string
): Promise<{ uploads: {[originalFileName: string]: string} }> => {
    const urlFetch = `${BASE_URL}/api/bulk-treatment/upload-batch-media`
    const formData = new FormData()
    files.forEach((file) => {
        formData.append('files', file)
    })
    formData.append('project_id', projectId)

    return apiCall(
        urlFetch,
        'POST',
        formData,
        true
    )
}

export const launchBulkTreatment = (
    bqProjectId: string,
    bqDatasetId: string,
    bqTableName: string,
    columns: string[],
    promptRules: APIPromptRule[],
    imageColumn: string,
    mediaType: MediaType,
    treatmentName: string,
    projectId: string,
    removeBackground: boolean = false
): Promise<any> => {
    const urlFetch = `${BASE_URL}/api/bulk-treatment/launch`
    return apiCall(
        urlFetch,
        'POST',
        JSON.stringify({
            project_id: projectId,
            bq_project_id: bqProjectId,
            bq_dataset_id: bqDatasetId,
            bq_table_name: bqTableName,
            columns: columns,
            media_type: mediaType,
            prompt_rules: promptRules,
            image_column: imageColumn,
            treatment_name: treatmentName,
            remove_background: removeBackground,
        }),
        false
    )
}

export const downloadBulkTreatment = (
    treatmentId: string,
    projectId: string
): Promise<any> => {
    const urlFetch = `${BASE_URL}/api/bulk-treatment/download`
    return apiCall(
        urlFetch,
        'POST',
        JSON.stringify({
            project_id: projectId,
            treatment_id: treatmentId
        }),
        false
    )
}

// =============================================================================
// PATTERN-BASED BULK TREATMENT APIs
// =============================================================================

export interface PatternStepMeta {
    prompt?: string;
    model?: string;
    source_picture_id?: string;
    additionnal_pictures?: Array<{ id: string; category: string }>;
    target_size_w?: number;
    target_size_h?: number;
    factor?: string;
    edit_hotspot_x?: number;
    edit_hotspot_y?: number;
    directions?: string[];
    output_ratios?: string[];
    input_language?: string;
    output_languages?: string[];
    formats?: string[];
    use_brand_guidelines?: boolean;
    [key: string]: any; // Allow other properties
}

export interface PatternStep {
    picture_id: string;
    seq: number;
    op: string;
    meta?: PatternStepMeta;
    // Legacy fields (for backward compatibility, prefer using meta)
    model?: string;
    prompt?: string;
    source_picture_id?: string;
    additionnal_pictures?: Array<{ id: string; category: string }>;
    target_size_w?: number;
    target_size_h?: number;
    factor?: string;
    edit_hotspot_x?: number;
    edit_hotspot_y?: number;
}

export interface PatternPreviewResponse {
    pattern_project_id: string;
    pattern_thread_id: string;
    from_picture_id: string;
    to_picture_id: string;
    steps_count: number;
    steps: PatternStep[];
}

export interface PromptOverride {
    find: string;
    replace: string;  // Can contain {column_name} variables
}

export interface PatternConfig {
    pattern_project_id: string;
    pattern_thread_id: string;
    from_picture_id: string;
    to_picture_id: string;
    prompt_overrides?: Record<string, PromptOverride[]>;  // step_index -> replacements
    additionnal_pictures?: Array<{
        category: string;
        source: 'column' | 'fixed';
        column_name?: string;  // BQ column containing image URL
        fixed_url?: string;    // Fixed image URL
    }>;
}

export interface PatternRule {
    id: string;
    condition: {
        column: string;
        operator: string;
        value: string;
    };
    pattern_config: PatternConfig;
}

/**
 * Get a preview of pattern steps between two pictures
 */
export const getPatternPreview = (
    patternProjectId: string,
    patternThreadId: string,
    fromPictureId: string,
    toPictureId: string
): Promise<PatternPreviewResponse> => {
    const urlFetch = `${BASE_URL}/api/projects/${patternProjectId}/threads/${patternThreadId}/pattern?from_picture_id=${fromPictureId}&to_picture_id=${toPictureId}`
    return apiCall(
        urlFetch,
        'GET'
    )
}

/**
 * Launch a pattern-based bulk treatment
 */
export const launchPatternBulkTreatment = (
    bqProjectId: string,
    bqDatasetId: string,
    bqTableName: string,
    columns: string[],
    imageColumn: string,
    treatmentName: string,
    projectId: string,
    patternRules: PatternRule[]
): Promise<any> => {
    const urlFetch = `${BASE_URL}/api/bulk-treatment/launch`
    return apiCall(
        urlFetch,
        'POST',
        JSON.stringify({
            project_id: projectId,
            bq_project_id: bqProjectId,
            bq_dataset_id: bqDatasetId,
            bq_table_name: bqTableName,
            columns: columns,
            image_column: imageColumn,
            treatment_name: treatmentName,
            treatment_mode: 'pattern',
            pattern_rules: patternRules,
            media_type: 'image'
        }),
        false
    )
}

/**
 * Test process a single row with pattern rules (synchronous, returns base64 image)
 * Useful for previewing the result before launching a full bulk treatment
 */
export interface ProcessSingleRowResponse {
    status: 'success' | 'error' | 'skipped';
    rule_id: string;
    error_message: string;
    output_base64?: string;
    output_url?: string;
}

export const testProcessSingleRow = (
    rowData: Record<string, any>,
    patternRules: PatternRule[],
    imageColumn: string,
    projectId: string
): Promise<ProcessSingleRowResponse> => {
    const urlFetch = `${BASE_URL}/api/bulk-treatment/process-single-row`
    return apiCall(
        urlFetch,
        'POST',
        JSON.stringify({
            row_data: rowData,
            pattern_rules: patternRules,
            image_column: imageColumn,
            project_id: projectId,
            save_to_gcs: false
        }),
        false
    )
}
